import { useState, useEffect } from "react";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import TestEditModal from "@modals/TestEditModal"; // Adjust the import path as needed
import Helpers from "../Helpers";
import { toast } from "react-toastify";
import { LuChevronsDown, LuChevronsUp } from "react-icons/lu";
import ClientSortableTable from "@elements/ClientSortableTable";
import TestDetailsModal from "@modals/TestDetailsModal"; // Adjust the import path as needed
import Enums from "@/enums";
import JobDescriptionModal from "@modals/JobDescriptionModal";
import JobInfoModal from "@modals/JobInfoModal";
import EFDetailsModal from "@modals/EFDetailsModal";
import { FaFileDownload, FaFileUpload, FaPlusSquare } from "react-icons/fa";
import { BsClipboardPlus } from "react-icons/bs";
import { billableRowClass } from "@/utils/testRowHelpers";
import { downloadAuthenticatedFile } from "@/utils/authenticatedFileDownload";
import { GrUserWorker } from "react-icons/gr";
import { SiTestrail } from "react-icons/si";
import RequestModal from "@modals/RequestModal";
import { useDashboardSignalR } from "@/utils/useDashboardSignalR";
import { copyUrlAsLink } from "@/utils/copyUrlAsLink";
import { formatElapsedDdHhMm } from "@/utils/formatElapsedDdHhMm";
import { useIntervalTick } from "@/utils/useIntervalTick";

export default function CompanyDashboardTab(props) {
    const { company, activeTab, updateSpinner, adminPerms, updateActiveTab } = props.tabData;
    const isDashboardTab = activeTab === "dashboard";
    const [isEditModalOpen, setIsModalOpen] = useState(false);
    const [selectedTests, updateSelectedTests] = useState([]);
    const [allTestStatuses, updateStatuses] = useState(null);
    const [testInstanceId, setTestInstanceId] = useState(null);
    const [detailsActiveTab, setDetailsActiveTab] = useState("details");
    const [dashboardData, updateDashboardData] = useState(null);
    const [jobInfoJobId, setJobInfoJobId] = useState(null);
    const [jobDescriptionJobId, setJobDescriptionJobId] = useState(null);
    const [dragOverJobIndex, setDragOverJobIndex] = useState(null); // highlight drop target
    const [efDetailsTestId, setEfDetailsTestId] = useState(null);
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
    const [selectedStage, setSelectedStage] = useState("pending");
    const [signalRMessages, setSignalRMessages] = useState<{
        eventType?: string;
        companyName?: string;
        companyId?: number;
        testId?: number;
        testInstanceId?: number;
        receivedAt?: string;
        firstName?: string;
        lastName?: string;
        userName?: string;
    }[]>([]);
    const hasActiveTests = Array.isArray(dashboardData?.activeTests) && dashboardData.activeTests.length > 0;
    useIntervalTick(5000, isDashboardTab && hasActiveTests);

    useEffect(() => {
        if (isDashboardTab) {
            getDashboardDataAsync();
        }
    }, [activeTab]);

    useEffect(() => {
        if (!dashboardData) return;

        const availableStages = [
            Array.isArray(dashboardData?.pendingTests) ? "pending" : null,
            Array.isArray(dashboardData?.activeTests) ? "active" : null,
            Array.isArray(dashboardData?.gradableTests) ? "gradable" : null,
            Array.isArray(dashboardData?.recentTests) ? "recent" : null,
            Array.isArray(dashboardData?.efPendings) ? "efpending" : null,
        ].filter(Boolean);

        if (availableStages.length === 0) return;
        if (!availableStages.includes(selectedStage)) {
            setSelectedStage(availableStages[0]);
        }
    }, [dashboardData, selectedStage]);

    function formatApplicantLabel(test) {
        const fullName = [String(test?.firstName || "").trim(), String(test?.lastName || "").trim()]
            .filter(Boolean)
            .join(" ");

        return fullName || String(test?.userName || "").trim();
    }

    function findApplicantForToast(eventData, snapshot) {
        const testCollections = [
            snapshot?.recentTests,
            snapshot?.gradableTests,
            snapshot?.pendingTests,
            snapshot?.activeTests,
            snapshot?.efPendings,
        ];

        for (const tests of testCollections) {
            if (!Array.isArray(tests)) continue;

            const match = tests.find((test) => {
                const matchesTestId = eventData?.testId && Number(test?.testId || 0) === Number(eventData.testId);
                const matchesInstanceId = eventData?.testInstanceId && Number(test?.testInstanceId || 0) === Number(eventData.testInstanceId);
                return matchesTestId || matchesInstanceId;
            });

            const applicantLabel = formatApplicantLabel(match);
            if (applicantLabel) {
                return {
                    label: applicantLabel,
                    firstName: String(match?.firstName || "").trim(),
                    lastName: String(match?.lastName || "").trim(),
                    userName: String(match?.userName || "").trim(),
                };
            }
        }

        return null;
    }

    useDashboardSignalR(async (data) => {
        if (!isOnThisTab()) return;
        if (!data?.companyId || data.companyId === company?.id) {
            const refreshedData = await getDashboardDataAsync();
            const applicant = findApplicantForToast(data, refreshedData) || findApplicantForToast(data, dashboardData);
            const payloadName = [data?.firstName, data?.lastName].filter(Boolean).join(" ").trim();
            const applicantLabel = applicant?.label || payloadName;
            const label = applicantLabel || (data?.companyName ?? (data?.companyId ? `Company ${data.companyId}` : "Unknown"));
            const testLabel = data?.testId ? ` (Test #${data.testId})` : "";
            if (data?.eventType === "testStarted") toast.info(`Test started for ${label}${testLabel}`);
            else if (data?.eventType === "testCreated") toast.info(`New test created for ${label}${testLabel}`);
            else if (data?.eventType === "testCompleted") toast.info(`Test completed for ${label}${testLabel}`);
            else if (data?.eventType === "testGraded") toast.success(`Test graded for ${label}${testLabel}`);
            setSignalRMessages(prev => {
                const cutoff = Date.now() - 24 * 60 * 60 * 1000;
                const enrichedMessage = {
                    ...data,
                    firstName: data?.firstName || applicant?.firstName,
                    lastName: data?.lastName || applicant?.lastName,
                    userName: data?.userName || applicant?.userName,
                };

                return [enrichedMessage, ...prev.filter(m =>
                    !(m.companyId === data?.companyId && m.testId === data?.testId) &&
                    (m.receivedAt ? new Date(m.receivedAt).getTime() >= cutoff : true)
                )].slice(0, 20);
            });
        }
    }, { enabled: isDashboardTab });

    // Don't render until we're on the dashboard tab AND data is loaded
    if (!isDashboardTab) return null;

    function isOnThisTab() {
        return isDashboardTab;
    }

    // function getData() {
    //     if (isOnThisTab() && company && dashboardData) {
    //         if (!intervalId && dashboardData.perms.includes(Enums.ROLE_PERMS.CompanyMenuTestRecents)) {
    //             getStatusesAsync();
    //             setIntervalId(
    //                 setInterval(() => {
    //                     if (isOnThisTab()) {
    //                         getStatusesAsync();
    //                     } else
    //                         resetInterval();
    //                 }, 90000)
    //             )
    //         }
    //     } else {
    //         resetInterval();
    //     }
    // }

    async function onSetStatusAsync(e) {
        const testId = parseInt(e.target.getAttribute("data-testId"));
        const testInstanceId = parseInt(e.target.getAttribute("data-testinstanceid"));
        const data = {
            testId: testId,
            testInstanceId: testInstanceId,
            statusCode: e.target.value,
        };
        updateSpinner(true);
        const resp = await RightTimeApi.putAsync("/TestStatus", data);
        updateSpinner(false);
        if (resp && resp.ok) {
            toast.success("Test status updated.");
            await getDashboardDataAsync();
        }
    }
    async function setJobStatusAsync(job, statusId) {
        try {
            if (!job?.id) {
                toast.error("Missing job id.");
                return;
            }
            const jobId = job.id;
            const status = parseInt(statusId, 10);

            if (Number.isNaN(status)) {
                toast.error("Invalid status.");
                return;
            }

            if (updateSpinner) updateSpinner(true);
            const url = `/Job/${jobId}/Status/${status}`;
            const resp = await RightTimeApi.putAsync(url);
            if (resp && resp.ok) {
                toast.success("Status updated.");
                await getDashboardDataAsync();
            } else {
                toast.error("Failed to update status.");
            }
        } catch (err) {
            console.error("Error updating JD status:", err);
            toast.error("Error updating status.");
        } finally {
            if (updateSpinner) updateSpinner(false);
        }
      }

    async function setDescriptionStatus(item, statusId) {
        try {
            const description = Helpers.JobDescription(item);
            if (!item?.id || !description?.id) {
                toast.error("Missing job or description id.");
                return;
            }
            const jobId = item.id;
            const descId = description.id;
            const status = parseInt(statusId, 10);

            if (Number.isNaN(status)) {
                toast.error("Invalid status.");
                return;
            }

            if (updateSpinner) updateSpinner(true);
            const url = `/Job/${jobId}/Description/${descId}/Status/${status}`;
            const resp = await RightTimeApi.putAsync(url);
            if (resp && resp.ok) {
                toast.success("Description status updated.");
                await getDashboardDataAsync();
            } else {
                toast.error("Failed to update description status.");
            }
        } catch (err) {
            console.error("Error updating description status:", err);
            toast.error("Error updating description status.");
        } finally {
            if (updateSpinner) updateSpinner(false);
        }
    }

    async function copyEFToClipboard(job) {
        if (!job || !job.descriptions || job.descriptions.length === 0)
            return;
        const baseURL = Helpers.Env("VITE_TEST_RUNNER_URL");
        let description = Helpers.JobEfDescription(job);
        let url = baseURL + description.efCode;
        if (!url || url.length === 0) {
            return;
        }

        try {
            const copied = await copyUrlAsLink(url);
            if (copied) {
                toast.success("Copied URL to clipboard");
            } else {
                toast.error("Could not copy URL to clipboard");
            }
        } catch (err) {
            toast.error("Could not copy URL: ", err);
        }
    }      
    
    function handleDownloadDescriptionAttachment(job) {
        const mostRecentDesc = Helpers.JobDescription(job);
        if (mostRecentDesc && 
            mostRecentDesc.attachmentFileName && 
            mostRecentDesc.attachmentId && 
            job.id) {
          Helpers.downloadJobDescriptionAttachmentToFolder(job.id, mostRecentDesc);
        }
      }


    async function handleFileUploadForJob(job) {
        const mostRecentDesc = Helpers.JobDescription(job);
        if (!job.id) {
            toast.error("Missing job information.");
            return;
        }

        // Create a temporary file input
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.onchange = async (event) => {
            const file = (event.target as HTMLInputElement)?.files?.[0];
            if (file) {
                try {
                    if (updateSpinner) updateSpinner(true);
                    const fileResp = await RightTimeApi.postFileAsync(`/Job/${job.id}/File`, file);
                    
                    if (fileResp && fileResp.ok) {
                        toast.success("File uploaded successfully!");
                        await getDashboardDataAsync(); // Refresh the data
                    } else {
                        toast.error("Failed to upload file.");
                    }
                } catch (error) {
                    console.error('Error uploading file:', error);
                    toast.error("Error uploading file.");
                } finally {
                    if (updateSpinner) updateSpinner(false);
                }
            }
        };
        fileInput.click();
    }

    function renderJobs() {
        return (
            <ClientSortableTable
                columns={[
                    { key: "id", label: "Id", filterable: false, sortable: false },
                    { key: "name", label: "Job", filterable: false, sortable: false },
                    { key: "state", label: "Status", filterable: true, sortable: true },
                    { key: "descriptionStatus", label: "Description Status", filterable: true, sortable: true },
                    { key: "File", label: "Desc", filterable: false, sortable: false },
                    { key: "efLink", label: "BE Link", filterable: false, sortable: false },
                ]}
                data={dashboardData.jobs}
                tableClass="clientSortable clientFilterable expanded"
                caption={
                    <>
                        <span onClick={onToggleCollapse} className="tableCaption">Jobs
                            <span className="captionCount">({dashboardData.jobs.length.toString()})</span>
                        </span>
                        {dashboardData.jobs ? (
                            <span className="captionRight">
                                <FaPlusSquare 
                                    className="actionIcon plusIcon" 
                                    onClick={(e) => { e.stopPropagation(); setJobInfoJobId(0); }} 
                                    title="Add Job"
                                />
                                <LuChevronsDown className="collapse" />
                                <LuChevronsUp className="expand" />
                            </span>
                        ) : null}
                    </>
                }
                onCaptionClick={onToggleCollapse}
                renderRow={(job, index) => {
                    let description = Helpers.JobDescription(job);
                    let efDescription = Helpers.JobEfDescription(job);
                    return (
                        <tr key={index} >
                            <td onClick={() => setJobInfoJobId(job.id)}>{job.id}</td>
                            <td onClick={() => setJobInfoJobId(job.id)}>{job.name}</td>
                            {dashboardData.perms.includes(Enums.ROLE_PERMS.ApproveJobDescription) ? (
                                <td>
                                    <select value={job.stateId} onChange={(e)=>setJobStatusAsync(job, e.target.value)} data-jobid={job.id}>
                                    {dashboardData.jobStates.map((s) => (<option key={s.id} value={s.id}>{s.displayName}</option>))}
                                    </select>
                                </td>
                                ):(
                                <td onClick={() => setJobInfoJobId(job.id)}>{Helpers.JobStateName(job)}</td>
                            )}
                            {job.stateId === Enums.JOB_STATE.PendingDescription ? (
                                dashboardData.perms.includes(Enums.ROLE_PERMS.ApproveJobDescription) ? (
                                    <td>
                                        <select 
                                            value={description?.stateId || Enums.JOB_DESCRIPTION_STATE.PendingDescription} 
                                            onChange={(e) => setDescriptionStatus(job, e.target.value)}
                                        >
                                            {dashboardData.jobDescStates.map((state) => (
                                                <option key={state.id} value={state.id}>
                                                    {state.displayName}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    ) : (
                                    <td onClick={() => setJobDescriptionJobId(job.id)}>
                                        {Helpers.DescriptionStateName(job)}
                                    </td>
                                )
                            ) : (
                                <td></td>
                            )}
                            <td
                                className={`drop-target ${dragOverJobIndex === index ? 'drag-over' : ''}`}
                                onDragOver={(e) => { e.preventDefault(); }}
                                onDragEnter={() => setDragOverJobIndex(index)}
                                onDragLeave={() => setDragOverJobIndex(null)}
                                onDrop={(e) => handleJobFileDrop(job, e)}
                                title="Drop a file to upload"
                                onClick={() => {
                                    if (description?.attachmentId) {
                                        handleDownloadDescriptionAttachment(job);
                                    } else {
                                        handleFileUploadForJob(job);
                                    }
                                }}
                            >
                                <span title={description?.attachmentId ? "Download file" : "Upload file"}>
                                    {description?.attachmentId ? (
                                        <FaFileDownload />
                                ) : <FaFileUpload />}
                                </span>
                            </td>
                            <td onClick={() => copyEFToClipboard(job)}>
                                { efDescription?.efCode ? (
                                    <>
                                        <BsClipboardPlus className="pointer" />
                                        <span className="badge">{job.eFs.length}</span>
                                    </>
                                ) : null}
                            </td>
                        </tr>
                    );
                }}
            />
        );
    }

    async function handleJobFileDrop(item, e) {
        e.preventDefault();
        setDragOverJobIndex(null);

        try {
            const dt = e.dataTransfer;
            if (!dt || !dt.files || dt.files.length === 0) {
                toast.error("No file detected.");
                return;
            }
            const file = dt.files[0];

            if (!item?.description?.companyJobDescriptionId) {
                toast.error("No description available to attach the file.");
                return;
            }

            if (updateSpinner) updateSpinner(true);
            const resp = await RightTimeApi.postFileAsync(`/Job/${item.id}/Description/${item?.description?.companyJobDescriptionId}/File`, file);
            if (resp && resp.ok) {
                toast.success("File uploaded.");
                await getDashboardDataAsync();
            } else {
                toast.error("Failed to upload file.");
            }
        } catch (err) {
            console.error("Error uploading file:", err);
            toast.error("Error uploading file.");
        } finally {
            if (updateSpinner) updateSpinner(false);
        }
    }

    // async function getStatusesAsync() {
    //     const resp = await RightTimeApi.getAsync(`/TestStatuses/${company?.id}`);
    //     if (resp && resp.ok && resp.jsonData) {
    //         updateStatuses(resp.jsonData);
    //     }
    // }

    async function getDashboardDataAsync() {
        if (updateSpinner) updateSpinner(true);
        const resp = await RightTimeApi.getAsync(`/Dashboard/${company?.id}`);
        if (updateSpinner) updateSpinner(false);
        if (resp && resp.ok && resp.jsonData) {
            updateDashboardData(resp.jsonData);
            return resp.jsonData;
        }

        return null;
    }

    async function onToggleCollapse(e) {
        const tableWrapper = e.target.closest(".tableWrapper"); // Find the closest parent with the class "tableWrapper"
        if (tableWrapper) 
            tableWrapper.classList.toggle("collapsed"); // Toggle the "collapsed" class
    }

    async function onCloseTestEditModalAsync() {
        setIsModalOpen(false);
        updateSelectedTests([]);
        await getDashboardDataAsync();
    }

    async function closeScoreModal() {
        setTestInstanceId(null);
        setDetailsActiveTab("details");
        await getDashboardDataAsync();
    }

    async function showScoreModal(e) {
        // Find the closest td element to handle clicks on child elements

        const cell = e.target.closest('td');
        if (cell) {
            const testInstanceId = cell.getAttribute("data-testinstanceid");
            if (testInstanceId) {
                setTestInstanceId(parseInt(testInstanceId));
                setDetailsActiveTab("scores");
            }
        }
    }

    const downloadFile = async (item, event) => {
        // Prevent row click when clicking the download icon
        event.stopPropagation();

        if (!item) {
            toast.error("No file ID available for download.");
            return;
        }

        try {
            const jdId = item.description?.companyJobDescriptionId ?? item.description?.id;
            if (!item.id || !jdId) {
                toast.error("No file ID available for download.");
                return;
            }
            const ok = await downloadAuthenticatedFile(
                `/CompanyJob/${item.id}/Description/${jdId}/File`,
                item.description?.attachmentFileName || "download"
            );
            if (ok) toast.success("File downloaded successfully!");
            else toast.error("Error downloading file.");
        } catch (error) {
            toast.error("Error downloading file.");
            console.error('Error downloading file:', error);
        }
    }

    function renderRecentTests() {
        return (
            <ClientSortableTable
                columns={[
                    { key: "testId", label: "TestId", filterable: false, sortable: true },
                    { key: "siteName", label: "Site", filterable: true, sortable: true },
                    { key: "testType", label: "Test Type", filterable: true, sortable: true },
                    { key: "userName", label: "User", filterable: true, sortable: true },
                    { key: "languageAbbrev", label: "Lang", filterable: false, sortable: false },
                    { key: "created", label: "Created", filterable: false, sortable: false },
                    { key: "completed", label: "Completed", filterable: false, sortable: false },
                    { key: "status", label: "Status", filterable: true, sortable: true },
                    { key: "scores", label: "Scores", filterable: false, sortable: false },
                ]}
                data={dashboardData.recentTests}
                tableClass="clientSortable clientFilterable expanded"
                caption={
                    <>
                        <span onClick={onToggleCollapse} className="tableCaption">
                            Assessments Recently Completed
                            <span className="captionCount">({dashboardData.recentTests.length.toString()})</span>
                        </span>
                        <span className="captionRight">
                            {dashboardData.recentTests ? (
                                <>
                                    <LuChevronsDown className="collapse" />
                                    <LuChevronsUp className="expand" />
                                </>
                            ) : null}
                        </span>
                    </>
                }
                onCaptionClick={onToggleCollapse}
                renderRow={(item, index) => {
                    const s = dashboardData.testStatuses?.filter((status) => status.companyTestTypeId == item.companyTestTypeId) || [];
                    return (
                        <tr key={index} data-testinstanceid={item.testInstanceId} onClick={() => setTestInstanceId(item.testInstanceId)} className={billableRowClass(item.billable)}>
                            <td className="underline">{item.testId}</td>
                            <td>{item.siteName}</td>
                            <td>{item.testTypeName || item.testType}</td>
                            <td>
                                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : "")}
                            </td>
                            <td>{item.languageAbbrev}</td>


                            <td>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
                            <td>{Helpers.EST(item.completed || item.submitTime, "MM/dd/yyyy")}</td>
                            <td>
                                <select
                                    value={item?.companyTestStatusId ?? ""}
                                    data-testid={item.testId}
                                    data-testinstanceid={item.testInstanceId}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onChange={onSetStatusAsync}
                                    className={billableRowClass(item.billable)}
                                >
                                    <option key={`empty-${item.testId}`} value="">
                                        {/* Empty option */}
                                    </option>
                                    {s.map((status) => {
                                        return (
                                            <option
                                                key={`${item.testId}-${status.ctsId}`}
                                                value={status.ctsId}
                                                className={billableRowClass(status.billable)}
                                            >
                                                {status.display}
                                            </option>
                                        );
                                    })}
                                </select>
                            </td>
                            <td data-testinstanceid={item.testInstanceId} onClick={showScoreModal}
                                className={`underline ${item.scores && item.scores.some(score => score.message) ? 'has-tooltip' : ''}`}>
                                {item.scores && item.scores.length > 0
                                    ? item.scores.filter(s => s.score && s.score.length > 0).map((score, idx) => {
                                        let typeName = "";
                                        switch (score.scoreType) {
                                            case 1:
                                                typeName = "Company";
                                                break;
                                            case 2:
                                                typeName = "Applicant";
                                                break;
                                            case 3:
                                                typeName = "Overall";
                                                break;
                                            default:
                                                typeName = "Unknown";
                                        }
                                        return (
                                            <div key={score.id || idx}>
                                                {typeName}: {score.score}
                                            </div>
                                        );
                                    })
                                    : null}
                            </td>
                        </tr>
                    );
                }}
            />);
    }

    function renderGradableTests() {
        return (
            <ClientSortableTable
                columns={[
                    { key: "testId", label: "TestId", filterable: false, sortable: true },
                    { key: "testType", label: "Test Type", filterable: true, sortable: true },
                    { key: "siteName", label: "Site", filterable: true, sortable: true },
                    { key: "userName", label: "User", filterable: true, sortable: true },
                    { key: "languageAbbrev", label: "Lang", filterable: false, sortable: false },
                    { key: "created", label: "Created", filterable: false, sortable: false },
                    { key: "completed", label: "Completed", filterable: false, sortable: false },
                    { key: "status", label: "Status", filterable: true, sortable: true },
                    { key: "scores", label: "Scores", filterable: false, sortable: false },
                ]}
                data={dashboardData.gradableTests}
                tableClass="clientSortable clientFilterable expanded"
                caption={
                    <>
                        <span onClick={onToggleCollapse} className="tableCaption">
                            Assessments Pending Grading
                            <span className="captionCount">({dashboardData.gradableTests.length.toString()})</span>
                        </span>
                        <span className="captionRight">
                            {dashboardData.gradableTests ? (
                                <>
                                    <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
                                </>
                            ) : null}
                        </span>
                    </>
                }
                onCaptionClick={onToggleCollapse}
                renderRow={(item, index) => {
                    const s = allTestStatuses?.filter((status) => status.companyId == item.companyId && status.companyTestTypeId == item.companyTestTypeId) || [];
                    return (
                        <tr key={index} data-testinstanceid={item.testInstanceId} onClick={() => setTestInstanceId(item.testInstanceId)} className={billableRowClass(item.billable)}>
                            <td className="underline">{item.testId}</td>
                            <td>{item.testTypeName || item.testType}</td>
                            <td>{item.siteName}</td>
                            <td>
                                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '')}
                            </td>
                            <td>{item.languageAbbrev}</td>
                            <td>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
                            <td>{Helpers.EST(item.completed || item.submitTime, "MM/dd/yyyy")}</td>
                            <td>
                                <select
                                    value={item.status ?? ""}
                                    data-testid={item.testId}
                                    data-testinstanceid={item.testInstanceId}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onChange={onSetStatusAsync}
                                    className={billableRowClass(item.billable)}
                                >
                                    <option key={`empty-${item.testId}`} value=""></option>
                                    {s.map((status) => (
                                        <option key={`${item.testId}-${status.companyTestStatusId}`} value={status.code}>
                                            {status.code}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td data-testinstanceid={item.testInstanceId} onClick={showScoreModal}
                                className={`underline ${item.scores && item.scores.some(score => score.message) ? 'has-tooltip' : ''}`}>
                                {item.scores && item.scores.length > 0
                                    ? item.scores.filter(s => s.score && s.score.length > 0).map((score, idx) => {
                                        let typeName = "";
                                        switch (score.scoreType) {
                                            case 1:
                                                typeName = "Company";
                                                break;
                                            case 2:
                                                typeName = "Applicant";
                                                break;
                                            case 3:
                                                typeName = "Overall";
                                                break;
                                            default:
                                                typeName = "Unknown";
                                        }
                                        return (
                                            <div key={score.id || idx}>
                                                {typeName}: {score.score}
                                            </div>
                                        );
                                    })
                                    : null}
                            </td>
                        </tr>
                    );
                }}
            />);
    }



    function renderPendingTests() {
        return (
            <ClientSortableTable
                columns={[
                    { key: "testId", label: "TestId", filterable: false, sortable: true },
                    { key: "siteName", label: "Site", filterable: true, sortable: true },
                    { key: "testType", label: "Test Type", filterable: true, sortable: true },
                    { key: "created", label: "Created", filterable: false, sortable: false },
                    { key: "userName", label: "User", filterable: true, sortable: true },
                    { key: "codeExpiration", label: "Code Expiration", filterable: false, sortable: false },
                ]}
                data={dashboardData.pendingTests}
                tableClass="clientSortable clientFilterable expanded"
                caption={
                    <>
                        <span onClick={onToggleCollapse} className="tableCaption">
                            Assessments Pending User
                            <span className="captionCount">({dashboardData.pendingTests.length.toString()})</span>
                        </span>
                        <span className="captionRight">
                            {dashboardData.pendingTests ? (
                                <>
                                    <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
                                </>
                            ) : null}
                        </span>
                    </>
                }
                onCaptionClick={onToggleCollapse}
                renderRow={(item, index) => {
                    return (
                        <tr key={index} data-testinstanceid={item.testInstanceId} onClick={() => setTestInstanceId(item.testInstanceId)} className={billableRowClass(item.billable)}>
                            <td className="underline">{item.testId}</td>
                            <td>{item.siteName}</td>
                            <td>{item.testTypeName || item.testType}</td>
                            <td title={Helpers.EST(item.created || item.createTime, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
                            <td>
                                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '')}
                            </td>
                            <td title={Helpers.EST(item.codeExpiration, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.codeExpiration, "MM/dd/yyyy")}</td>
                        </tr>
                    );
                }}
            />);
    }

    function renderActiveTests() {
        return (
            <ClientSortableTable
                columns={[
                    { key: "testId", label: "TestId", filterable: false, sortable: true },
                    { key: "siteName", label: "Site", filterable: true, sortable: true },
                    { key: "testType", label: "Test Type", filterable: true, sortable: true },
                    { key: "status", label: "Status", filterable: true, sortable: true },
                    { key: "created", label: "Created", filterable: false, sortable: false },
                    { key: "completed", label: "Completed", filterable: false, sortable: false },
                    { key: "duration", label: "Duration", subLabel: "(dd:hh:mm)", filterable: false, sortable: false },
                    { key: "userName", label: "User", filterable: true, sortable: true },
                ]}
                data={dashboardData.activeTests}
                tableClass="clientSortable clientFilterable expanded"
                caption={
                    <>
                        <span onClick={onToggleCollapse} className="tableCaption">
                            Assessments In Progress
                            <span className="captionCount">({dashboardData.activeTests.length.toString()})</span>
                        </span>
                        <span className="captionRight">
                            {dashboardData.activeTests ? (
                                <>
                                    <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
                                </>
                            ) : null}
                        </span>
                    </>
                }
                onCaptionClick={onToggleCollapse}
                renderRow={(item, index) => {
                    const s = allTestStatuses?.filter((status) => status.companyId == item.companyId && status.companyTestTypeId == item.companyTestTypeId) || [];
                    const duration = formatElapsedDdHhMm(item.startTime, Date.now());
                    return (
                        <tr key={index} data-testinstanceid={item.testInstanceId} onClick={() => setTestInstanceId(item.testInstanceId)} className={billableRowClass(item.billable)}>
                            <td className="underline">{item.testId}</td>
                            <td>{item.siteName}</td>
                            <td>{item.testTypeName || item.testType}</td>
                            <td>
                                <select 
                                    value={item.status ?? ""} 
                                    data-testid={item.testInstanceId} 
                                    data-testinstanceid={item.testInstanceId}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onChange={onSetStatusAsync}
                                    className={billableRowClass(item.billable)}
                                >
                                    <option key={`empty-${item.testId}`} value=""></option>
                                    {s.map((status) => (
                                        <option key={`${item.testId}-${status.companyTestStatusId}`} value={status.code}>
                                            {status.code}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td title={Helpers.EST(item.created || item.createTime, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
                            <td title={Helpers.EST(item.completed || item.submitTime, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.completed || item.submitTime, "MM/dd/yyyy")}</td>
                            <td title={duration}>{duration}</td>
                            <td>
                                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '')}
                            </td>
                        </tr>
                    );
                }}
            />);
    }

    function renderEfPending() {
        return (
            <ClientSortableTable
                columns={[
                    { key: "Name", label: "Name", filterable: false, sortable: false },
                    { key: "Job", label: "Job", filterable: false, sortable: false },
                    { key: "location", label: "Location", filterable: true, sortable: true },
                    { key: "created", label: "Created", filterable: false, sortable: false },
                ]}
                data={dashboardData.efPendings}
                tableClass="clientSortable clientFilterable expanded"
                caption={
                    <>
                        <span onClick={onToggleCollapse} className="tableCaption">
                            Pending BEs
                            <span className="captionCount">({dashboardData.efPendings.length.toString()})</span>
                        </span>
                        <span className="captionRight">
                            {dashboardData.edfPendings ? (
                                <>
                                    <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
                                </>
                            ) : null}
                        </span>
                    </>
                }
                onCaptionClick={onToggleCollapse}

                renderRow={(item, index) => {
                    return (
                        <tr 
                            key={index} 
                            onClick={() => setEfDetailsTestId(item.testId)}
                            className="selectable"
                        >
                            <td>{item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : ''}</td>
                            <td>{item.jobName}</td>
                            <td>{item.location}</td>
                            <td>{Helpers.EST(item.testDate, "MM/dd/yyyy")}</td>
                        </tr>
                    );
                }}
                >
                </ClientSortableTable>
        );
    }

    async function onCloseEFDetailsModal() {
        updateSpinner(true);
        setEfDetailsTestId(null);
        await getDashboardDataAsync();
    }

    function renderSignalRMessages() {
        if (signalRMessages.length === 0) return null;
        const eventLabels = { testStarted: "Test Started", testCreated: "Test Created", testCompleted: "Test Completed", testGraded: "Test Graded" };
        return (
            <div className="tableWrapper dimensionTable scrollable collapsable">
                <table>
                    <caption>
                        <span onClick={onToggleCollapse} className="title">
                            Live Events
                            <span className="captionCount">({signalRMessages.length})</span>
                        </span>
                        <span className="captionRight">
                            <LuChevronsDown className="collapse" />
                            <LuChevronsUp className="expand" />
                        </span>
                    </caption>
                    <thead>
                        <tr>
                            <th>Time</th>
                            <th>Event</th>
                            <th>Name</th>
                            <th>Test ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {signalRMessages.map((msg, i) => (
                            <tr key={i}>
                                <td title={Helpers.EST(msg.receivedAt, "MM/dd/yyyy h:mm:ss a")}>
                                    {Helpers.EST(msg.receivedAt, "h:mm:ss a")}
                                </td>
                                <td>{eventLabels[msg.eventType] ?? msg.eventType}</td>
                                <td>{[msg.firstName, msg.lastName].filter(Boolean).join(" ") || msg.userName || "—"}</td>
                                <td>{msg.testId ?? "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="companyDashboardTab">
            <div className="dashboard-actions">
                <button 
                    className="btn btn-primary"
                    onClick={() => setJobInfoJobId(0)}
                    title="Add a new job"
                >
                    <GrUserWorker /> Add Job
                </button>
                <button 
                    className="btn btn-primary"
                    onClick={() => setIsRequestModalOpen(true)}
                    title="Create a new test"
                >
                    <SiTestrail /> Create Test
                </button>
            </div>
            {adminPerms?.includes(254) && renderSignalRMessages()}
            <div className="dashboardStageCards">
                {[
                    Array.isArray(dashboardData?.pendingTests) ? { id: "pending", label: "Assessments Pending User", data: dashboardData.pendingTests } : null,
                    Array.isArray(dashboardData?.activeTests) ? { id: "active", label: "Assessments In Progress", data: dashboardData.activeTests } : null,
                    Array.isArray(dashboardData?.gradableTests) ? { id: "gradable", label: "Assessments Pending Grading", data: dashboardData.gradableTests } : null,
                    Array.isArray(dashboardData?.recentTests) ? { id: "recent", label: "Assessments Recently Completed", data: dashboardData.recentTests } : null,
                    Array.isArray(dashboardData?.efPendings) ? { id: "efpending", label: "Pending BEs", data: dashboardData.efPendings } : null,
                ].filter(Boolean).map((stage: any) => (
                    <button
                        key={stage.id}
                        type="button"
                        className={`dashboardStageCard${selectedStage === stage.id ? " isActive" : ""}`}
                        onClick={() => setSelectedStage(stage.id)}
                    >
                        <div className="stageCardLabel">{stage.label}</div>
                        <div className="stageCardCount">{stage.data?.length ?? 0}</div>
                    </button>
                ))}
            </div>

            <div className="dashboardStageDetail">
                {selectedStage === "pending" && dashboardData?.pendingTests && renderPendingTests()}
                {selectedStage === "active" && dashboardData?.activeTests && renderActiveTests()}
                {selectedStage === "gradable" && dashboardData?.gradableTests && renderGradableTests()}
                {selectedStage === "recent" && dashboardData?.recentTests && renderRecentTests()}
                {selectedStage === "efpending" && dashboardData?.efPendings && renderEfPending()}
            </div>
            <TestEditModal isOpen={isEditModalOpen} onClose={onCloseTestEditModalAsync} checkedItems={selectedTests} updateSpinner={updateSpinner} />
            {testInstanceId !== null && (
                <TestDetailsModal 
                    isOpen={true} 
                    onClose={closeScoreModal} 
                    testInstanceId={testInstanceId} 
                    updateSpinner={updateSpinner} 
                    isSystemAdmin={adminPerms?.includes(254) || false} 
                    initialActiveTab={detailsActiveTab} 
                    adminPerms = {props.adminPerms}
                />
            )}
            <JobDescriptionModal jobId={jobDescriptionJobId} jobDescriptionId={null} updateSpinner={updateSpinner} onClose={() => setJobDescriptionJobId(null)} />
            <JobInfoModal jobId={jobInfoJobId} company={company} adminPerms={adminPerms} updateSpinner={updateSpinner} onClose={async () => { setJobInfoJobId(null); await getDashboardDataAsync(); }} />
            <EFDetailsModal 
                testId={efDetailsTestId} 
                onClose={onCloseEFDetailsModal} 
                title="BE Details" 
            />
            <RequestModal 
                isOpen={isRequestModalOpen}
                title="Create Test"
                onClose={() => setIsRequestModalOpen(false)}
                companyId={company?.id}
                company={company}
                updateSpinner={updateSpinner}
                onCreated={async () => { setIsRequestModalOpen(false); await getDashboardDataAsync(); }}
                adminPerms={adminPerms}
            />
        </div>
    );
}
